import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    type AlertAction,
    AlertBanner,
    type ButtonProps,
    toast,
    useScreenType,
} from '@gitbook/spine-react';

import { apiClient } from '../../state';

type OrganizationOpenBillingReason = Extract<
    analytics.AnalyticsEvent,
    { type: 'organization_open_billing' }
>['reason'];

const DEFAULT_PAYMENT_FAILED_DETAILS = (
    <>
        We couldn't process your last payment, which means we cannot make changes to your
        subscription.
        <br />
        Please check your billing page for more information.
    </>
);

type PaymentFailedActionButtonProps = Omit<
    Pick<ButtonProps, 'label' | 'href' | 'onPress' | 'trailingIcon' | 'loadable'>,
    'label'
> & {
    label: string;
};

export function getPaymentFailedDetails(args: {
    canAdminBilling: boolean;
    canCheckout: boolean;
    includeSupportHint?: boolean;
}) {
    const { canAdminBilling, canCheckout, includeSupportHint = false } = args;

    if (!canAdminBilling) {
        return `We couldn't process your payment. Ask an organization admin to check billing to keep your organization active.`;
    }

    const details = canCheckout
        ? `We couldn't process your payment. Check your billing page for more information.`
        : `We couldn't process your payment. Check your payment method to keep your organization active.`;

    return includeSupportHint
        ? `${details.slice(0, -1)}, or get in touch with support to find out more.`
        : details;
}

async function openOrganizationBillingPortal(args: {
    organizationId: string;
    analyticsReason: OrganizationOpenBillingReason;
}) {
    const { organizationId, analyticsReason } = args;
    analytics.logEvent(
        analytics.createOrgEvent(organizationId, {
            type: 'organization_open_billing',
            reason: analyticsReason,
        })
    );

    await toast.catch(async () => {
        const { data } = await apiClient.billing.getOrganizationBillingPortal(organizationId);
        window.location.href = data.url;
    }, 'Error while accessing billing portal');
}

export function getPaymentFailedPaymentMethodActionButtonProps(args: {
    organization: api.Organization;
    analyticsReason: OrganizationOpenBillingReason;
}): Pick<PaymentFailedActionButtonProps, 'label' | 'onPress' | 'trailingIcon' | 'loadable'> | null {
    const { organization, analyticsReason } = args;

    if (!organization.permissions.admin) {
        return null;
    }

    return {
        label: 'Check payment method',
        trailingIcon: Icon.ArrowUpRight,
        loadable: true,
        onPress: async () => {
            await openOrganizationBillingPortal({
                organizationId: organization.id,
                analyticsReason,
            });
        },
    };
}

export function getPaymentFailedActionButtonProps(args: {
    organization: api.Organization;
    analyticsReason: OrganizationOpenBillingReason;
}): PaymentFailedActionButtonProps | null {
    const { organization, analyticsReason } = args;

    if (!organization.permissions.admin) {
        return null;
    }

    if (organization.billing?.canCheckout) {
        return {
            label: 'Check billing',
            href: {
                overlay: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab: { type: 'plans' },
                },
            },
        };
    }

    return getPaymentFailedPaymentMethodActionButtonProps({
        organization,
        analyticsReason,
    });
}

function getPaymentFailedAlertAction(args: {
    organization: api.Organization;
    analyticsReason: OrganizationOpenBillingReason;
    kind?: AlertAction['kind'];
}): AlertAction | undefined {
    const { kind = 'danger', ...rest } = args;
    const action = getPaymentFailedActionButtonProps(rest);
    if (!action) {
        return undefined;
    }
    return {
        kind,
        ...action,
    };
}

type PaymentFailedAlertProps =
    | {
          organization: api.Organization;
          analyticsReason: OrganizationOpenBillingReason;
          action?: AlertAction;
          details?: React.ReactNode;
          includeSupportHint?: boolean;
      }
    | {
          organization?: undefined;
          analyticsReason?: never;
          action?: AlertAction;
          details?: React.ReactNode;
          includeSupportHint?: boolean;
      };

export function PaymentFailedAlert(props: PaymentFailedAlertProps = {}) {
    const { organization, action, details, includeSupportHint = false, analyticsReason } = props;
    const computedDetails =
        details ??
        (organization?.billing
            ? getPaymentFailedDetails({
                  canAdminBilling: organization.permissions.admin,
                  canCheckout: organization.billing.canCheckout,
                  includeSupportHint,
              })
            : DEFAULT_PAYMENT_FAILED_DETAILS);
    const computedAction =
        action ??
        (organization?.billing
            ? getPaymentFailedAlertAction({
                  organization,
                  analyticsReason,
              })
            : undefined);

    return (
        <Alert
            kind="danger"
            title="Payment failed"
            details={computedDetails}
            action={computedAction}
        />
    );
}

export function PaymentFailedAlertBanner(props: {
    organization: api.Organization;
    analyticsReason: OrganizationOpenBillingReason;
}) {
    const { organization, analyticsReason } = props;
    const { isMobile, isTablet } = useScreenType();
    if (!organization.billing?.hasPaymentFailed) {
        return null;
    }

    const details = isMobile
        ? undefined
        : isTablet
          ? `We couldn't process your payment.`
          : getPaymentFailedDetails({
                canAdminBilling: organization.permissions.admin,
                canCheckout: organization.billing.canCheckout,
            });

    return (
        <AlertBanner
            kind="danger"
            title="Payment failed"
            details={details}
            action={getPaymentFailedAlertAction({
                organization,
                analyticsReason,
                kind: 'danger',
            })}
        />
    );
}
